/** Responsive copies of a local project image, produced by `npm run optimize-images`. */
export interface LocalImageVariants {
  /** Smallest thumbnail: the fallback `src`, and what gets preloaded. */
  src: string;
  srcSet: string;
  width: number;
  height: number;
  /** Tiny blurred placeholder (data URI). */
  lqip: string;
}

/** A project defined in `public/projects.json` (as opposed to one managed in Sanity). */
export interface LocalProject {
  id: string;
  title: string;
  slug: string;
  imageUrl: string;
  location?: string;
  client?: string;
  scopeOfWork?: string;
  commencement?: string;
  finalizationDate?: string;
  category: string;
  subCategory?: string[];
  /** Present when thumbnails exist for this image; otherwise use `imageUrl` as is. */
  variants?: LocalImageVariants;
}

const LOCAL_PROJECTS_URL = '/projects.json';
const THUMBNAILS_URL = '/project-thumbs.json';

async function fetchJson<T>(url: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { signal });
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}: ${response.status} ${response.statusText}`);
  }
  return response.json();
}

const fileNameOf = (url: string) => url.split('/').pop() ?? '';

export async function fetchLocalProjects(signal?: AbortSignal): Promise<LocalProject[]> {
  const [projects, thumbnails] = await Promise.all([
    fetchJson<LocalProject[]>(LOCAL_PROJECTS_URL, signal),
    // Thumbnails are an optimization: without the manifest, projects still work with their full image.
    fetchJson<Record<string, LocalImageVariants>>(THUMBNAILS_URL, signal).catch(() => ({})),
  ]);

  return projects.map((project) => ({ ...project, variants: thumbnails[fileNameOf(project.imageUrl)] }));
}
